import { useState } from 'react';

export interface BrowserStorageOptions {
  key: string;
  initialValue?: string;
  storageType: 'sessionStorage' | 'localStorage';
}

export function useBrowserStorage<T>(options: BrowserStorageOptions) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage by key
      const item = window[options.storageType].getItem(options.key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : options.initialValue;
    } catch (error) {
      console.error(error);

      // remove item if json parse cannot be passed
      window[options.storageType].removeItem(options.key);
      return options.initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setItem = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);
      // Save to local storage

      const valueString = JSON.stringify(valueToStore);

      if (valueString) {
        window[options.storageType].setItem(
          options.key,
          JSON.stringify(valueToStore)
        );
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(error);
    }
  };

  // remove item from localstorage
  const removeItem = () => {
    window[options.storageType].removeItem(options.key);
  };

  return { value: storedValue, setItem, removeItem } as const;
}
